import React from 'react'
import { BaseWidget } from '../BaseWidget'
import { WidgetProps } from '../types'
import { Flex, Icon, Typography } from 'components'
import { WidgetTitle } from '../Atoms'
import { UilArrowDown, UilArrowRight, UilArrowUp } from '@iconscout/react-unicons'
import { Divider } from '@evrekadev/evreka-ui-components'
import { useTranslation } from 'utils/useTranslation'
import { TypographySkeleton } from 'components/Skeletons/TypographySkeleton'

type RankHeader = {
  key: string
  label?: string
}

type RankRowData = Record<string, string | number>

type RankData = {
  headers: Array<RankHeader>
  items: Array<RankRowData>
}

export type RankWidgetProps = WidgetProps<RankData>

export const RankWidget: React.FC<RankWidgetProps> = (props) => {
  return (
    <BaseWidget size={props.size}>
      <Content {...props} />
    </BaseWidget>
  )
}

const Content: React.FC<RankWidgetProps> = (props) => {
  if (props.size === '2x2') {
    return <LGContent {...props} />
  } else if (props.size === '1x2') {
    return <MDContent {...props} />
  }
  return <SMContent {...props} />
}

const SMContent: React.FC<RankWidgetProps> = (props) => {
  const { t } = useTranslation('pages.widgets')
  const firstItem = props.data?.items?.[0]
  const primaryHeader = props.data?.headers?.[0]
  const secondaryHeader = props.data?.headers?.[1]

  return (
    <Flex directionColumn="zero" between="zero" fullHeight>
      <WidgetTitle>{props.title}</WidgetTitle>
      <Flex directionColumn="zero" gap={0.5}>
        {props.loading ? (
          <TypographySkeleton height="header_md" />
        ) : (
          <Typography fontSize="header_md" fontWeight="semibold" color="grey900">
            {primaryHeader ? firstItem?.[primaryHeader.key] : '-'}
          </Typography>
        )}
        {props.loading ? (
          <TypographySkeleton height="text_md" />
        ) : (
          <Typography fontSize="text_md" color="grey500">
            {secondaryHeader ? firstItem?.[secondaryHeader.key] : '-'}{' '}
            {secondaryHeader?.label ?? t(secondaryHeader?.key)}
          </Typography>
        )}
      </Flex>
    </Flex>
  )
}

const MDContent: React.FC<RankWidgetProps> = (props) => {
  const { t } = useTranslation('pages.widgets')
  const firstItem = props.data?.items?.[0]
  const primaryHeader = props.data?.headers?.[0]
  const secondaryHeader = props.data?.headers?.[1]

  const data = props.data
  return (
    <Flex directionColumn={'zero'} gap={1} fullHeight>
      <WidgetTitle>{props.title}</WidgetTitle>
      <Flex gap={0.5} fullHeight fullWidth>
        <Flex
          directionColumn="zero"
          gap={0.5}
          fullHeight
          bottom="zero"
          left="zero"
          style={{
            flex: 1,
          }}
        >
          {props.loading ? (
            <TypographySkeleton height="text_md" width="7rem" />
          ) : (
            <Typography fontSize="text_md" color="grey500">
              {secondaryHeader && firstItem?.[secondaryHeader.key]}{' '}
              {secondaryHeader?.label ?? t(secondaryHeader?.key)}
            </Typography>
          )}
          {props.loading ? (
            <TypographySkeleton height="header_md" width="5rem" />
          ) : (
            <Typography variant="span" fontSize="header_md" fontWeight="semibold" color="grey900">
              {primaryHeader && firstItem?.[primaryHeader.key].toString().slice(0, 10)}
            </Typography>
          )}
        </Flex>
        <Flex
          directionColumn="zero"
          gap={0.25}
          fullHeight
          style={{
            flex: 1,
            overflow: 'hidden',
          }}
        >
          {!props.loading &&
            primaryHeader &&
            secondaryHeader &&
            data &&
            data?.items?.slice(1, 5).map((item, index) => {
              return (
                <React.Fragment key={index}>
                  <RankRow
                    index={index + 1}
                    cells={[item[primaryHeader.key], item[secondaryHeader.key]]}
                  />
                  {index !== data.items?.length - 2 && <Divider />}
                </React.Fragment>
              )
            })}
          {props.loading &&
            new Array(4).fill(0).map((_, index) => {
              return (
                <React.Fragment key={index}>
                  <TypographySkeleton height="text_sm" width="10rem" />
                  {index !== 4 && <Divider />}
                </React.Fragment>
              )
            })}
        </Flex>
      </Flex>
    </Flex>
  )
}

const LGContent: React.FC<RankWidgetProps> = (props) => {
  const { t } = useTranslation('pages.widgets')
  let _total = 0
  const primaryHeader = props.data?.headers?.[0]
  const secondaryHeader = props.data?.headers?.[1]
  props.data?.items?.forEach((item) => {
    if (secondaryHeader) {
      _total += +item[secondaryHeader.key]
    }
  })

  const data = props.data
  return (
    <Flex directionColumn="zero" gap={1} fullHeight>
      <Flex directionColumn="zero" between="zero" style={{ flexGrow: 1 }}>
        <WidgetTitle>{props.title}</WidgetTitle>
        {/* {props.loading ? (
          <TypographySkeleton height="header_md" width="4rem" />
        ) : (
          <Typography fontSize="header_md" fontWeight="semibold" color="grey900">
            {total}
          </Typography>
        )} */}
      </Flex>
      <Flex directionColumn="zero" gap={0.75}>
        {primaryHeader && secondaryHeader && (
          <Flex between="zero">
            <Typography fontSize="text_lg" fontWeight="medium" color="grey700">
              {primaryHeader.label ?? t(primaryHeader.key)}
            </Typography>
            <Typography fontSize="text_lg" fontWeight="medium" color="grey700">
              {secondaryHeader.label ?? t(secondaryHeader.key)}
            </Typography>
          </Flex>
        )}
        {!props.loading &&
          primaryHeader &&
          secondaryHeader &&
          data?.items?.map((item, index) => {
            return (
              <React.Fragment key={index}>
                <RankRow
                  index={index}
                  cells={[item[primaryHeader.key], item[secondaryHeader.key]]}
                />
                {index !== data.items?.length - 1 && <Divider />}
              </React.Fragment>
            )
          })}
        {props.loading &&
          new Array(5).fill(0).map((_, index) => {
            return (
              <React.Fragment key={index}>
                <TypographySkeleton height="text_sm" width="10rem" />
                {index !== 4 && <Divider />}
              </React.Fragment>
            )
          })}
      </Flex>
    </Flex>
  )
}

type RankRowProps = {
  cells: Array<string | number>
  variant?: 'better' | 'worse' | 'equal'
  index: number
}
const RankRow: React.FC<RankRowProps> = ({ cells, index }) => {
  return (
    <Flex
      fullHeight
      style={{
        flexGrow: 1,
      }}
      gap={0.5}
      middle="zero"
    >
      <Typography
        fontSize="text_sm"
        fontWeight={index === 0 ? 'bold' : 'medium'}
        className="jetbrains-mono"
        color={index === 0 ? 'green500' : 'grey500'}
      >
        {index + 1}
      </Typography>
      {cells.map((cell, i) => {
        return (
          <Typography
            style={{
              flexGrow: i === 0 ? 1 : 0, // left text grows
              flexShrink: i === 0 ? 1 : 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            fontSize={'text_sm'}
            fontWeight={i === 0 ? 'regular' : 'bold'}
            color={index === 0 ? 'green700' : 'grey700'}
            key={i}
            noWrap
            className={i === 1 ? 'jetbrains-mono' : ''}
          >
            {cell}
          </Typography>
        )
      })}
    </Flex>
  )
}

const _RankIcon: React.FC<{ variant?: RankRowProps['variant'] }> = ({ variant }) => {
  switch (variant) {
    case 'better':
      return <Icon icon={UilArrowUp} color="green500" size={20} />
    case 'worse':
      return <Icon icon={UilArrowDown} color="red500" size={20} />
    case 'equal':
      return <Icon icon={UilArrowRight} color="orange500" size={20} />
    default:
      return null
  }
}
